import { setReqHeaders } from 'utils/common';
import { WIDGET_API } from 'utils/constants';
import { addPwPrefixToBaseUrl } from 'utils/pwContext';
import { getSessionId } from 'utils/session';

import type { ReservationCardStatus } from '../types';

/**
 * Card-proof upload.
 *
 * The path is the backend's existing `kyc-verify` endpoint — the contract confirms it is unchanged
 * for two-stage deposits, so there is no reservation-specific upload.
 *
 * Called through this feature's own thin fetch layer rather than `checkoutAPI.useCardVerification`
 * on purpose: sharing the *URL* is unavoidable (it is the backend's), but sharing the *module*
 * would put a `kyc_card_verification` import in this directory, and either feature may be deleted
 * independently. The cost is one `fetch` wrapper; the benefit is that removing either feature
 * leaves the other compiling.
 *
 * Everything else this feature needs already exists:
 *   - card status + outstanding hold → `GET /v1/user-cards/{amount}?pay_group_id=`
 *     (`kyc_verification_status`, `has_reserved_deposit`)
 *   - account verification           → `cashier_settings.user_verified` with init
 *   - hold state                     → `PaymentStatus` on the `/notifications` hub
 *   - verification result            → `KycVerificationResult` on the `/verifications` hub
 *
 * `card-verification` (REQ.03) is deliberately absent: that requirement is scoped to BE only. It
 * extends the 3P protocol between the Platform and Payments Core, which then calls Asquad's
 * `/v1/payment/challenge/two-steps-verification`. The cashier is not part of that exchange.
 */
export const RESERVATION_ENDPOINTS = {
  cardVerify: '/user-card/kyc-verify',
  cardCheckStatus: '/user-card/kyc-verify/check-status',
} as const;

export interface CardVerifyRequest {
  file: File;
  cardId?: number;
  cardPan?: string;
  saveCard?: boolean;
}

export interface CardVerifyResponse {
  card_id: number;
  kyc_verification_status: ReservationCardStatus;
}

const buildUrl = (path: string): string => `${addPwPrefixToBaseUrl(WIDGET_API)}${path}`;

const buildHeaders = (endpoint: string): Headers => {
  const headers = new Headers();

  // Same session header the shared base query sets, so the request is indistinguishable
  // from an RTK Query one on the wire. `Content-Type` is intentionally left unset: for multipart
  // the browser must add the boundary itself; JSON requests set it at the call site.
  setReqHeaders(headers, { sessionId: getSessionId(), endpoint });

  return headers;
};

/** Uploads the document that confirms card ownership. */
export const verifyReservationCard = async ({
  file,
  cardId,
  cardPan,
  saveCard,
}: CardVerifyRequest): Promise<CardVerifyResponse> => {
  const formData = new FormData();

  // The contract accepts either a stored card id or a raw PAN plus the save intent — never both.
  if (cardId) {
    formData.append('card_id', String(cardId));
  } else if (cardPan) {
    formData.append('card_pan', cardPan);
    formData.append('save_card', String(!!saveCard));
  }

  formData.append('file', file);

  const response = await fetch(buildUrl(RESERVATION_ENDPOINTS.cardVerify), {
    method: 'POST',
    headers: buildHeaders(RESERVATION_ENDPOINTS.cardVerify),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Reservation card verification failed: ${response.status}`);
  }

  return (await response.json()) as CardVerifyResponse;
};

/** Verification status of a freshly typed card — same response as `kyc-verify`, JSON body with `card_pan` only. */
export const checkReservationCardStatus = async (cardPan: string): Promise<CardVerifyResponse> => {
  const headers = buildHeaders(RESERVATION_ENDPOINTS.cardCheckStatus);
  headers.set('Content-Type', 'application/json');

  const response = await fetch(buildUrl(RESERVATION_ENDPOINTS.cardCheckStatus), {
    method: 'POST',
    headers,
    body: JSON.stringify({ card_pan: cardPan }),
  });

  if (!response.ok) {
    throw new Error(`Reservation card status check failed: ${response.status}`);
  }

  return (await response.json()) as CardVerifyResponse;
};
